<div mat-dialog-title class="mat-dialog-header">
    <h2 class="dialog-title">{{ isEditing ? 'Edit Custom Regime' : 'New Custom Regime' }}</h2>
</div>

<mat-dialog-content [formGroup]="formGroup" class="pv-storage-regime-form">

    <div class="pv-storage-regime-columns">

        <div class="pv-storage-regime-left">

            <mat-form-field appearance="outline" class="pv-storage-regime-full">
                <mat-label>Crops</mat-label>
                <mat-select formControlName="cropId">
                    <mat-option *ngFor="let crop of cropOptions" [value]="crop.id">
                        <mat-icon [svgIcon]="'common:' + crop.id"></mat-icon>
                        <span>{{ crop.label }}</span>
                    </mat-option>
                </mat-select>
                <mat-hint>The schedule dialog shows this regime for this crop.</mat-hint>
                <mat-error>Select a crop.</mat-error>
            </mat-form-field>

            <mat-form-field *ngIf="!isEditing" appearance="outline" class="pv-storage-regime-full">
                <mat-label>Start from</mat-label>
                <mat-select [formControl]="sourceRegimeControl">
                    <mat-option [value]="null">Start from scratch</mat-option>
                    <mat-option *ngFor="let regime of startFromOptions" [value]="regime.key">
                        {{ regime.config?.label }} <span *ngIf="regime.isGlobal">(Legacy Library)</span>
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <div class="pv-storage-regime-form-grid">
                <mat-form-field appearance="outline">
                    <mat-label>Label</mat-label>
                    <input matInput formControlName="label" maxlength="255" required>
                </mat-form-field>


                <mat-form-field appearance="outline" class="pv-storage-regime-full">
                    <mat-label>Description</mat-label>
                    <textarea matInput formControlName="description"></textarea>
                </mat-form-field>

                <mat-form-field appearance="outline">
                    <mat-label>Overdue after (days)</mat-label>
                    <input matInput type="number" min="0" formControlName="overdueThresholdDays">
                </mat-form-field>
            </div>

            <h3 class="pv-storage-regime-heading">Sample-level actions</h3>
            <p class="form-caption">Apply to the whole Sample, not to any one Stage.</p>

            <div formArrayName="sampleActions">
                <mat-card *ngFor="let action of sampleActions.controls; let i = index" [formGroupName]="i"
                          class="pv-storage-regime-stage">
                    <div class="pv-storage-regime-stage-header">
                        <span class="pv-storage-regime-stage-number">{{ action.get('type').value | titlecase }}</span>

                        <button mat-icon-button color="warn" type="button" (click)="removeSampleAction(i)" matTooltip="Remove action">
                            <mat-icon>delete</mat-icon>
                        </button>
                    </div>

                    <div class="pv-storage-regime-stage-grid">
                        <ng-container *ngIf="action.get('type').value === 'treatment'">
                            <mat-form-field appearance="outline" class="pv-storage-regime-full">
                                <mat-label>Treatment applied</mat-label>
                                <input matInput formControlName="treatmentApplied">
                            </mat-form-field>

                            <div class="pv-storage-regime-action-grid pv-storage-regime-full">
                                <mat-form-field appearance="outline">
                                    <mat-label>Time after harvest</mat-label>
                                    <input matInput type="number" min="0" step="1" formControlName="offsetValue">
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                    <mat-label>Unit</mat-label>
                                    <mat-select formControlName="offsetUnit">
                                        <mat-option value="days">Days</mat-option>
                                        <mat-option value="weeks">Weeks</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </ng-container>

                        <ng-container *ngIf="action.get('type').value === 'intake'">
                            <mat-form-field appearance="outline">
                                <mat-label>Target condition</mat-label>
                                <input matInput formControlName="intakeCondition">
                            </mat-form-field>
                            <mat-form-field appearance="outline">
                                <mat-label>Target temp. (&#8451;)</mat-label>
                                <input matInput type="number" formControlName="intakeTemperature">
                            </mat-form-field>
                        </ng-container>

                        <ng-container *ngIf="action.get('type').value === 'destroy'">
                            <mat-form-field appearance="outline" class="pv-storage-regime-full">
                                <mat-label>Due relative to stage</mat-label>
                                <mat-select formControlName="precedesStageKey">
                                    <mat-option [value]="null">No fixed date</mat-option>
                                    <mat-option *ngIf="lastStage" [value]="lastStage.control.get('stageKey').value">
                                        {{ lastStage.control.get('label').value }} (final stage)
                                    </mat-option>
                                </mat-select>
                                <mat-hint>Optional — Destroy can only be timed relative to the final Stage, never an earlier one</mat-hint>
                            </mat-form-field>

                            <div class="pv-storage-regime-action-grid pv-storage-regime-full" *ngIf="action.get('precedesStageKey').value">
                                <mat-form-field appearance="outline">
                                    <mat-label>Time after</mat-label>
                                    <input matInput type="number" min="0" step="1" formControlName="offsetValue">
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                    <mat-label>Unit</mat-label>
                                    <mat-select formControlName="offsetUnit">
                                        <mat-option value="days">Days</mat-option>
                                        <mat-option value="weeks">Weeks</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </ng-container>
                    </div>
                </mat-card>
            </div>

            <div class="pv-storage-regime-action-buttons">
                <button mat-stroked-button type="button" (click)="addSampleAction('intake')" [disabled]="!hasCrops">
                    <mat-icon>add</mat-icon>
                    Add intake
                </button>
                <button mat-stroked-button type="button" (click)="addSampleAction('treatment')" [disabled]="!hasCrops">
                    <mat-icon>add</mat-icon>
                    Add treatment
                </button>
                <button mat-stroked-button type="button" (click)="addSampleAction('destroy')" [disabled]="!hasCrops">
                    <mat-icon>add</mat-icon>
                    Add destroy
                </button>
            </div>

            <div class="pv-storage-regime-divider"></div>

            <h3 class="pv-storage-regime-heading">Stages</h3>

            <div formArrayName="stages">
                <mat-card *ngFor="let entry of stageEntries; trackBy: trackByControl" [formGroupName]="entry.index" class="pv-storage-regime-stage pv-storage-regime-evaluation-stage">
                    <div class="pv-storage-regime-stage-header">
                        <span class="pv-storage-regime-stage-number">{{ entry.control.get('label').value || 'Stage' }}</span>

                        <button mat-icon-button color="warn" type="button" (click)="removeStage(entry.index)" matTooltip="Remove stage">
                            <mat-icon>delete</mat-icon>
                        </button>
                    </div>

                    <div class="pv-storage-regime-stage-grid">
                        <mat-form-field appearance="outline">
                            <mat-label>Stage label</mat-label>
                            <input matInput formControlName="label" required>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="pv-storage-regime-colour-field">
                            <mat-label>Colour</mat-label>
                            <span matPrefix class="pv-storage-regime-swatch"
                                  [class.pv-storage-regime-swatch-empty]="!entry.control.get('color').value"
                                  [style.background-color]="entry.control.get('color').value"></span>
                            <input matInput formControlName="color" placeholder="No colour chosen"
                                   [colorPicker]="entry.control.get('color').value"
                                   [cpOutputFormat]="'hex'"
                                   [cpCancelButton]="true"
                                   [cpOKButton]="true"
                                   (colorPickerChange)="entry.control.get('color').setValue($event)">
                            <mat-error>Enter a hex colour, e.g. #FFFFFF</mat-error>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>Days after harvest</mat-label>
                            <input matInput type="number" min="0" step="1" formControlName="offsetValue" required>
                            <mat-error>Enter a whole number, zero or more.</mat-error>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>Unit</mat-label>
                            <mat-select formControlName="offsetUnit">
                                <mat-option value="days">Days</mat-option>
                                <mat-option value="weeks">Weeks</mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>

                    <!-- Scheduled Action chips, tethered below the Stage they precede — travels with
                         the Stage visually since it's nested inside the same stage card. -->
                    <div class="pv-storage-regime-scheduled-actions">
                        <mat-card *ngFor="let action of scheduledActionsForStage(entry.control.get('stageKey').value); trackBy: trackByControl"
                                  [formGroup]="action.control" class="pv-storage-regime-scheduled-action-chip">
                            <div class="pv-storage-regime-stage-header">
                                <span>{{ (action.control.get('type').value | titlecase) || 'Scheduled action' }}</span>
                                <button mat-icon-button color="warn" type="button" (click)="removeScheduledAction(action.index)" matTooltip="Remove">
                                    <mat-icon>delete</mat-icon>
                                </button>
                            </div>

                            <div class="pv-storage-regime-action-grid">
                                <mat-form-field appearance="outline">
                                    <mat-label>Type</mat-label>
                                    <mat-select formControlName="type">
                                        <mat-option *ngFor="let type of scheduledActionTypes" [value]="type">{{ type | titlecase }}</mat-option>
                                    </mat-select>
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                    <mat-label>Lead time</mat-label>
                                    <input matInput type="number" min="0" step="1" formControlName="offsetValue" required>
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                    <mat-label>Unit</mat-label>
                                    <mat-select formControlName="offsetUnit">
                                        <mat-option value="days">Days</mat-option>
                                        <mat-option value="weeks">Weeks</mat-option>
                                    </mat-select>
                                </mat-form-field>

                                <mat-checkbox formControlName="isAfter" class="pv-storage-regime-after-checkbox">After stage</mat-checkbox>

                                <mat-form-field appearance="outline" *ngIf="action.control.get('type').value === 'move'">
                                    <mat-label>Target condition</mat-label>
                                    <input matInput formControlName="moveCondition">
                                    <mat-hint>The environment the Sample is moving to, e.g. 'Cold Room'</mat-hint>
                                </mat-form-field>
                                <mat-form-field appearance="outline" *ngIf="action.control.get('type').value === 'move'">
                                    <mat-label>Target temp. (&#8451;)</mat-label>
                                    <input matInput type="number" formControlName="moveTemperature">
                                </mat-form-field>

                                <mat-form-field appearance="outline" class="pv-storage-regime-full" *ngIf="action.control.get('type').value === 'treatment'">
                                    <mat-label>Treatment applied</mat-label>
                                    <input matInput formControlName="treatmentApplied">
                                </mat-form-field>

                                <mat-form-field appearance="outline" *ngIf="action.control.get('type').value === 'packout'">
                                    <mat-label>Target condition</mat-label>
                                    <input matInput formControlName="packOutCondition">
                                </mat-form-field>
                                <mat-form-field appearance="outline" *ngIf="action.control.get('type').value === 'packout'">
                                    <mat-label>Target temp. (&#8451;)</mat-label>
                                    <input matInput type="number" formControlName="packOutTemperature">
                                </mat-form-field>
                            </div>
                        </mat-card>

                        <button mat-stroked-button type="button" [disabled]="!hasStages"
                                (click)="addScheduledAction(entry.control.get('stageKey').value)">
                            <mat-icon>add</mat-icon>
                            Add scheduled action
                        </button>
                    </div>
                </mat-card>
            </div>

            <button mat-stroked-button type="button" (click)="addStage()" [disabled]="!hasCrops">
                <mat-icon>add</mat-icon>
                Add stage
            </button>

        </div>

        <div class="pv-storage-regime-right">
            <h3 class="pv-storage-regime-heading pv-storage-regime-heading-first">Schedule preview</h3>

            <div *ngIf="graphErrors.length > 0" class="pv-storage-regime-preview-errors">
                <p>Fix the following before a preview is available:</p>
                <ul>
                    <li *ngFor="let error of graphErrors">{{ error }}</li>
                </ul>
            </div>

            <ng-container *ngIf="graphErrors.length === 0">
                <table class="pv-table" *ngIf="previewRows.length > 0">
                    <thead>
                        <tr>
                            <th>Label</th>
                            <th>Type</th>
                            <th>Date</th>
                            <th>Detail</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let row of previewRows; trackBy: trackByPreviewRow">
                            <td>{{ row.label }}</td>
                            <td>{{ row.type }}</td>
                            <td>{{ row.date | amDateFormat: 'll' }}</td>
                            <td>{{ row.detail }}</td>
                        </tr>
                    </tbody>
                </table>

                <p *ngIf="previewRows.length === 0">Add a stage to see its preview.</p>
            </ng-container>
        </div>

    </div>

</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button (click)="cancel()">Cancel</button>
    <button mat-raised-button color="primary" (click)="save()">Save</button>
</mat-dialog-actions>
